<script lang="ts" setup>
import { EvaluationTest } from "types/EvaluationTest";

const props = defineProps({
  evaluationTest: {
    type: Object as PropType<EvaluationTest>,
    required: true,
  },
  metricName: {
    type: String,
    required: true,
  },
  nlpResponse: {
    type: Array as PropType<any[]>,
    required: false,
  },
  googleData: {
    type: Object as PropType<any>,
    required: false,
  },
  isParsing: {
    type: Boolean,
    required: false,
    default: false,
  },
  isAnalyzingWithGoogle: {
    type: Boolean,
    required: false,
    default: false,
  },
});

const emit = defineEmits(["parse", "analyzeWithGoogle"]);

const primaryText = computed(() => {
  if (props.nlpResponse == undefined) return undefined;

  if (props.evaluationTest.originalReportedProblem.problem.relevantInstance) {
    const matchingInstance =
      props.evaluationTest.originalReportedProblem.problem.relevantInstance
        .text;

    return matchingInstance;
  }

  if (props.evaluationTest.originalReportedProblem.problem.text) {
    return props.evaluationTest.originalReportedProblem.problem.text;
  }

  return undefined;
});

const relevantData = computed(() => {
  if (primaryText.value == undefined) return undefined;

  return (props.nlpResponse as any[]).find((result: any) => {
    return result.text == primaryText.value;
  });
});

const relevantGoogleData = computed(() => {
  if (primaryText.value == undefined) return undefined;

  return (props.googleData.value.tokens as any[]).find((result: any) => {
    return result.text.content == primaryText.value;
  });
});

const keys = [
  "pos",
  "dep",
  "tag",
  "wordnet_pos",
  "head",
  "head_pos",
  "head_ix",
  "i",
  "lemma",
] as string[];

const keyLabelMap = {
  pos: "Part of Speech",
  dep: "Dependency",
  tag: "Tag",
  wordnet_pos: "Possible POS",
  head: "Head",
  head_pos: "Head POS",
  head_ix: "Head Index",
  i: "Index",
  lemma: "Lemma",
} as { [key: string]: string };
</script>

<template>
  <div class="flex flex-col overflow-auto grow min-h-0">
    <div class="flex flex-row justify-between">
      <BaseTextButton @click="$emit('parse')" :show-spinner="isParsing">
        Parse with Spacy
      </BaseTextButton>
      <BaseTextButton
        :show-spinner="isAnalyzingWithGoogle"
        @click="$emit('analyzeWithGoogle')"
      >
        Parse with Google
      </BaseTextButton>
    </div>
    <div class="my-2 mt-4 flex flex-items-center">
      <span class="font-bold mr-2"> Metric: </span>
      <span>
        {{ metricName }}
      </span>
    </div>
    <div class="my-4" v-if="evaluationTest.documentText">
      {{ evaluationTest.documentText }}
    </div>
    <div v-if="googleData" class="flex flex-col px-4">
      <div v-if="relevantGoogleData">
        <div class="mb-2">
          <span class="text-h5 underline">
            Google - {{ relevantGoogleData.text.content }}
          </span>
        </div>
        <div>
          <span class="font-bold"> Part of Speech: </span>
          <span>
            {{ relevantGoogleData.partOfSpeech.tag }}
          </span>
        </div>
        <div>
          <span class="font-bold"> Dependency </span>
          <span>
            {{ relevantGoogleData.dependencyEdge.label }}
          </span>
        </div>
      </div>
    </div>
    <div v-if="relevantData" class="flex flex-col p-4">
      <h2 class="text-h5 mb-2 underline">Spacy - {{ relevantData.text }}</h2>
      <div v-for="key in keys" class="flex flex-row items-center">
        <label class="text-body-1 font-bold mr-2">
          {{ keyLabelMap[key] }}:
        </label>
        <span class="">
          {{ relevantData[key] }}
        </span>
      </div>
    </div>

    <div v-if="nlpResponse" class="flex flex-col p-4">
      <h2 class="text-h4 pb-2 border-b my-4">Everything Else</h2>
      <div v-for="nlpData in nlpResponse" class="mb-4">
        <h2 class="text-h5 mb-2 underline">
          {{ nlpData.text }}
        </h2>
        <div v-for="key in keys" class="flex flex-row items-center">
          <label class="text-body-1 font-bold mr-2">
            {{ keyLabelMap[key] }}:
          </label>
          <span class="">
            {{ nlpData[key] }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped></style>
